import { Type } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ILookupService } from "../ILookupService";

export abstract class BaseLookupService<K, V extends CubiqTS.ConcreteEncodable>
  implements ILookupService<K, V>
{
  protected myItemMap: Map<any, any> = new Map();
  protected _isPrimed: boolean = false;

  constructor(protected lookupType: Type<V>) {}

  isPrimed(): boolean {
    return this._isPrimed;
  }

  prime(values: Array<V>, isComplete: boolean = true): void | Promise<void> {
    const pending: Promise<void>[] = [];
    if (values !== undefined && values !== null) {
      for (let i = 0; i < values.length; i++) {
        if (values[i] != null) {
          const result = this.mapItem(values[i]);
          if (result instanceof Promise) {
            pending.push(result);
          }
        } else {
          logger.out.warn("NULL order received while priming " + this.lookupType.name);
        }
      }
    }

    if (isComplete) {
      if (pending.length > 0) {
        return Promise.all(pending)
          .then(() => this.primeCompleted())
          .catch((err) => {
            logger.out.error(
              `${this.constructor.name}: mapItem promise rejected during prime: ${err}`,
            );
            this.primeCompleted();
          });
      } else {
        this.primeCompleted();
      }
    }
  }

  primeCompleted(): void {
    logger.out.info(
      `${this.constructor.name}: Priming complete - Entry Count = ${this.myItemMap?.size ?? 0}`,
    );
    this._isPrimed = true;
  }

  reset(): void {
    this._isPrimed = false;
    this.myItemMap.clear();
  }

  mapItem(item: V): void | Promise<void> {
    if (this.myItemMap.has(this.getItemKey(item))) {
      let oldItem = this.getItem(this.getItemKey(item));
      this.deleteItem(oldItem);
    }
    this.myItemMap.set(this.getItemKey(item), item);
  }

  replaceItem(oldId: K, item: V): void {
    if (this.myItemMap.has(oldId)) {
      let oldItem = this.getItem(oldId);
      this.deleteItem(oldItem, oldId);
    }
    this.mapItem(item);
  }

  mapItemById(item: V, id: K) {
    // usually not needed but useful when key id
    // doesn't match item contents or key is already known

    if (this.myItemMap.has(id)) {
      let oldItem = this.getItem(id);
      this.deleteItemById(id);
    }

    this.myItemMap.set(id, item);
  }

  abstract getItemKey(item: V): K;

  getItem(key: K): V {
    // FIXME: Allow for non-string id

    return this.myItemMap.get(key);
  }

  createItem(item: V): boolean {
    let key = this.getItemKey(item);

    if (!this.myItemMap.has(key)) {
      this.mapItem(item);

      return true;
    }

    return false;
  }

  updateItem(item: V): boolean {
    let key = this.getItemKey(item);

    if (this.myItemMap.has(key)) {
      this.mapItem(item);
      return true;
    }

    return false;
  }

  deleteItem(item: V, id: K = null): void {
    if (id !== null) this.myItemMap.delete(id);
    else this.myItemMap.delete(this.getItemKey(item));
  }

  // could be string or numeric id
  deleteItemById(id: K): void {
    var item = this.getItem(id);
    if (item) {
      this.deleteItem(item);
    }
  }

  getAll(): Array<V> {
    return Array.from(this.myItemMap.values());
  }

  postprocess(fn: (item: V, id: K, base: ILookupService<K, V>) => boolean): void {
    this.myItemMap.forEach((value, key) => {
      let remove = fn(value, key, this);

      if (remove) this.deleteItem(value, key);
    });
  }
}
